import React, { useEffect, useState } from "react";
import { Button, ButtonGroup, Divider, FilledInput, FormGroup, FormLabel, MenuItem, OutlinedInput, Select, Stack, TextField } from "@mui/material";
import User from "../../types/User";
import CrisisAlertIcon from '@mui/icons-material/CrisisAlert';
import ReviewsIcon from '@mui/icons-material/Reviews';
import { LoadingButton } from "@mui/lab";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Solution, SolutionState } from "../../types/Solutions";
import { ChangeProps } from "../Lessons/Show";
import Toolbox from "../Shared/Toolbox";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import PercentIcon from '@mui/icons-material/Percent';
import CategoryAutocomplete from "../Shared/CategoryAutocomplete";
import { Category } from "../../types/Lessons";

export type ActionProps = {
  solution: Solution;
  user: User;
  isUpdating: boolean;
}

const Actions = (props: ActionProps & ChangeProps) => {

  const OwnerActions = () => {
    const [defineTarget, shouldDefineTarget] = useState<boolean>(false);
    const [type, setType] = useState<"quantitative" | "qualitative">("quantitative");
    const [direction, setDirection] = useState<"reduce" | "increase">("reduce");
    const [item, setItem] = useState<"frequency" | "severity" | "amount">("amount");
    const [categories, setCategories] = useState<Category[]|null>();
    const [rate, setRate] = useState<number>();
    const [date, setDate] = useState<number>();
    const [canSave, setCanSave] = useState<boolean>(false);

    useEffect(() => {
      setCanSave(!!item && !!rate && rate > 0 && !!date && !!categories);
    }, [type, direction, item, date, rate, categories]);

    const save = () => {
      props.onSave({
        target_at: date!,
        measures: {
          type: type,
          direction: direction!,
          item: item!,
          rate: rate!,
          categories: categories!.map(category => category.id)
        }
      })
    }

    return <>
      <LoadingButton loading={props.isUpdating} variant="outlined" color="primary" size="small" disabled={typeof (props.solution?.owner) === "undefined"} onClick={() => shouldDefineTarget(true)} startIcon={<CrisisAlertIcon />}>Define target</LoadingButton>

      {defineTarget && <Toolbox title="Define target" maxWidth="sm" onClose={() => shouldDefineTarget(false)}>
        <FormGroup>
          <FormLabel>Choose whether you will measure effectiveness quantitatively or qualitatively:</FormLabel>

          <ButtonGroup size="small" sx={{ justifyContent: "center", my: 2 }}>
            <Button color="info" variant="contained">Quantitative</Button>
            <Button color="secondary" variant="outlined" disabled>Qualitative</Button>
          </ButtonGroup>
        
          <p>
            <Select
              variant="filled"
              value={direction}
              required
              onChange={e => setDirection(e.target.value)}
              sx={{ my: 1, mr: 1, ['.MuiFilledInput-input']: { pt: "0.7rem !important" } }}
            >
              <MenuItem value={"reduce"}>Reduce</MenuItem>
              <MenuItem value={"increase"}>Increase</MenuItem>
            </Select>
            the
            <Select
              variant="filled"
              value={item}
              onChange={e => setItem(e.target.value)}
              required
              sx={{ my: 1, mx: 1, ['.MuiFilledInput-input']: { pt: "0.7rem !important" } }}
            >
              <MenuItem value={"amount"}>Amount</MenuItem>
              <MenuItem value={"frequency"}>Frequency</MenuItem>
              <MenuItem value={"severity"}>Severity</MenuItem>
            </Select>
            of lessons learned<br />in the categories
            <CategoryAutocomplete placeholder="Select categories"
                                  value={categories ?? []}
                                  options={props.solution.lessons.flatMap(lesson => lesson.categories ?? []) ?? []}
                                  onChange={setCategories} />
            by
            <FilledInput
              value={rate}
              onChange={e => setRate(Number(e.target.value))}
              type="number"
              size="small"
              required
              endAdornment={<PercentIcon color="secondary" fontSize="small" />}
              sx={{ my: 1, mx: 1, maxWidth: 120, ['.MuiFilledInput-input']: { pt: "0.7rem !important" } }}
            />
            at the target date of
          </p>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker value={date ? dayjs.unix(date) : undefined} onChange={date => setDate(dayjs(date).unix())} minDate={dayjs()} label="Target date" sx={{ mt: 2 }} />
          </LocalizationProvider>
        </FormGroup>

        <Divider />

        <FormGroup>
          <Button variant="outlined" color="primary" disabled={!canSave} size="small" onClick={save}>Save</Button>
        </FormGroup>
      </Toolbox>}
    </>
  }

  const ApproverActions = () => {
    const [publish, shouldPublish] = useState<boolean>(false);
    const [mitigation, setMitigation] = useState<string>();

    return <>
      <LoadingButton loading={props.isUpdating} variant="outlined" color="success" size="small" onClick={() => shouldPublish(true)} startIcon={<ReviewsIcon />}>Publish solution</LoadingButton>

      { publish && <Toolbox title="Publish solution" maxWidth="sm" onClose={() => shouldPublish(false)}>
        <TextField variant="filled" multiline rows={3} placeholder="Mitigation" value={mitigation} onChange={e => setMitigation(e.currentTarget.value)} />
        <Button variant="outlined" color="success" disabled={!mitigation} onClick={() => props.onSave({mitigation: mitigation!})}>Publish</Button>
      </Toolbox> }
    </>
  }

  const renderContent = () => {
    switch (SolutionState[props.solution.state]) {
      case SolutionState.resolved:
        break;
      case SolutionState.defined:
        if (props.solution?.owner?.id === props.user.id) {
          return <ApproverActions />
        } break;
      default:
        return <OwnerActions />;
    }
  }

  return <Stack direction="row" spacing={1} justifyContent="center">{renderContent()}</Stack>
}

export default Actions;